import { canAnimateElement, readCssTimeMs } from '../../components/motion-policy.js';
import { cancelAnimations, playAnimation, settleAnimations } from '../../components/motion-runner.js';
import { showToast } from '../../components/toast.js';

const SAVED_MARK_MS = 2500;

/** A short `Saved` at the row that was just stored; the page-wide toast is for create and delete. */
export function flashRowSaved(mark, timerApi = globalThis) {
    if (!(mark instanceof HTMLElement)) {
        return;
    }

    mark.hidden = false;
    timerApi.setTimeout(() => {
        mark.hidden = true;
    }, SAVED_MARK_MS);
}

/**
 * An empty name is refused at the field, before a request: the message sits in the row's own error
 * slot (`aria-describedby` of the input) instead of a browser bubble or the section banner.
 * @returns {boolean} whether the name may be sent
 */
export function checkNameField(input, error) {
    if (!(input instanceof HTMLInputElement) || !(error instanceof HTMLElement)) {
        return true;
    }

    const empty = input.value.trim() === '';
    error.textContent = empty ? 'Enter a name.' : '';
    error.hidden = !empty;
    if (empty) {
        input.setAttribute('aria-invalid', 'true');
        input.focus();
    } else {
        input.removeAttribute('aria-invalid');
    }

    return !empty;
}

export function initDictionaryFeedback(root, windowRef = globalThis.window) {
    const animations = new WeakMap();
    const readLayout = (feedback) => {
        const styles = typeof globalThis.getComputedStyle === 'function'
            ? globalThis.getComputedStyle(feedback)
            : null;
        const styleValue = (property, fallback = '0px') => (
            styles?.getPropertyValue?.(property)?.trim() || fallback
        );

        return {
            borderBottomWidth: styleValue('border-bottom-width'),
            borderTopWidth: styleValue('border-top-width'),
            height: `${feedback.getBoundingClientRect?.().height || feedback.scrollHeight || 0}px`,
            marginBottom: styleValue('margin-bottom'),
            opacity: styleValue('opacity', '1'),
            paddingBottom: styleValue('padding-bottom'),
            paddingTop: styleValue('padding-top'),
        };
    };
    const cancel = (feedback) => {
        cancelAnimations([animations.get(feedback)]);
        animations.delete(feedback);
    };
    const hide = async (section) => {
        const feedback = section?.querySelector?.('[data-dictionary-feedback]');
        if (!(feedback instanceof HTMLElement) || feedback.hidden) {
            return;
        }

        const layout = readLayout(feedback);
        cancel(feedback);
        if (!canAnimateElement(feedback, windowRef)) {
            feedback.hidden = true;

            return;
        }

        const animation = playAnimation(feedback, [
            {
                ...layout,
                overflow: 'hidden',
                transform: 'translateY(0) scale(1)',
            },
            {
                height: '0px',
                marginBottom: '0px',
                paddingTop: '0px',
                paddingBottom: '0px',
                borderTopWidth: '0px',
                borderBottomWidth: '0px',
                opacity: 0,
                overflow: 'hidden',
                transform: 'translateY(-6px) scale(0.985)',
            },
        ], {
            duration: readCssTimeMs(
                feedback,
                '--dictionary-feedback-reveal-duration',
                240,
            ),
            easing: 'ease-in',
            fill: 'both',
        });
        if (animation === null) {
            feedback.hidden = true;

            return;
        }

        animations.set(feedback, animation);
        await settleAnimations([animation]);
        if (animations.get(feedback) === animation) {
            animation.cancel();
            animations.delete(feedback);
            feedback.hidden = true;
        }
    };
    const show = (section, message, tone = 'success') => {
        if (tone === 'success') {
            showToast(message, section?.ownerDocument ?? globalThis.document, windowRef);

            return;
        }

        const feedback = section?.querySelector?.('[data-dictionary-feedback]');
        const feedbackMessage = feedback?.querySelector?.('[data-dictionary-feedback-message]');
        if (!(feedback instanceof HTMLElement) || !(feedbackMessage instanceof HTMLElement)) {
            return;
        }

        cancel(feedback);
        feedbackMessage.textContent = message;
        feedback.dataset.tone = tone;
        feedback.hidden = false;
        if (canAnimateElement(feedback, windowRef)) {
            const layout = readLayout(feedback);
            const animation = playAnimation(feedback, [
                {
                    height: '0px',
                    marginBottom: '0px',
                    paddingTop: '0px',
                    paddingBottom: '0px',
                    borderTopWidth: '0px',
                    borderBottomWidth: '0px',
                    opacity: 0,
                    overflow: 'hidden',
                    transform: 'translateY(-6px) scale(0.985)',
                },
                {
                    ...layout,
                    overflow: 'hidden',
                    transform: 'translateY(0) scale(1)',
                },
            ], {
                duration: readCssTimeMs(
                    feedback,
                    '--dictionary-feedback-reveal-duration',
                    240,
                ),
                easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
                fill: 'both',
            });
            if (animation !== null) {
                animations.set(feedback, animation);
                void settleAnimations([animation]).then(() => {
                    if (animations.get(feedback) === animation) {
                        animation.cancel();
                        animations.delete(feedback);
                    }
                });
            }
        }
    };

    root.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        const closeButton = target.closest('[data-dictionary-feedback-close]');
        if (closeButton !== null) {
            void hide(closeButton.closest('[data-dictionary-section]'));
        }
    });

    return { hide, show };
}
